// SOR calls behind the My Work view ("Building a My Work view on the workqueue", SOR Confluence):
// five calls once per session (A2 → A3 → B1 / B2 / A4, plus A1 per type), one list query per screen
// change, and PATCH → transition → history for every assignment step. Nothing type-specific
// (states, labels, actions, entity types) is hard-coded; it all comes from these calls.
import {
  combineSorFilters,
  type SorFacet,
  type SorQueryPayload,
  type SorRecord,
} from '../hooks/useSorApi';
import { QUEUE_ENTITY_DEFINITION, QUEUE_ITEM_DEFINITION, type QueueEntityRecord } from './myWorkQueueSor';
import type { WorkQueueCreateOption } from './myWorkQueueTypes';

export const DIRECTORY_USER_DEFINITION = 'core.directory.user';
export const QUEUE_DEFINITION = 'core.workqueue.queue';
export const QUEUE_MEMBER_DEFINITION = 'core.workqueue.queue_member';
export const QUEUE_ACTION_DEFINITION = 'core.workqueue.queue_action';
export const QUEUE_ITEM_HISTORY_DEFINITION = 'core.workqueue.queue_item_history';

/** Replaced server-side with the caller's verified identity */
const PRINCIPAL_ID = { $principal: 'id' };
const PRINCIPAL_ISSUER = { $principal: 'issuer' };

export type QueueRole = 'OBSERVER' | 'AGENT' | 'SUPERVISOR';

const QUEUE_ROLE_RANK: Record<QueueRole, number> = { OBSERVER: 0, AGENT: 1, SUPERVISOR: 2 };

/** True when `role` is at least `required` (SUPERVISOR > AGENT > OBSERVER) */
export function queueRoleMeets(role: QueueRole | null | undefined, required: QueueRole | null | undefined): boolean {
  if (!role) {
    return false;
  }
  return QUEUE_ROLE_RANK[role] >= QUEUE_ROLE_RANK[required ?? 'AGENT'];
}

/** A row from a `select` query: own fields flat (`row['data.queueId']`), relation fields under `related` */
export type SorProjectedRow = Record<string, unknown> & {
  id: string;
  related?: Record<string, SorProjectedRelated | SorProjectedRelated[] | null>;
};

export type SorProjectedRelated = Record<string, unknown> & {
  id: string;
  definition?: string;
  related?: Record<string, SorProjectedRelated | SorProjectedRelated[] | null>;
};

const asString = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);
const asNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);
const asRelated = (value: unknown): SorProjectedRelated | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as SorProjectedRelated) : null;

/** Current time as SOR expects it: UTC with milliseconds, e.g. 2026-10-30T10:15:00.000Z */
export function sorNow(): string {
  return new Date().toISOString();
}

/**
 * Directory display names are often the raw subject id ("samlp|circuitry-dev|jane@x.com");
 * shows the part after the last "|".
 */
export function formatDirectoryName(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  const parts = name.split('|');
  return parts[parts.length - 1] || name;
}

// ---------------------------------------------------------------------------------------------
// A2: who am I
// ---------------------------------------------------------------------------------------------

export function buildMyUserQuery(): SorQueryPayload {
  return {
    definition: DIRECTORY_USER_DEFINITION,
    filter: {
      and: [
        { field: 'subjectId', op: 'eq', value: PRINCIPAL_ID },
        { field: 'identityIssuer', op: 'eq', value: PRINCIPAL_ISSUER },
      ],
    },
    include: ['roles'],
    page: { limit: 1 },
  };
}

export interface MyWorkUser {
  /** core.directory.user record id: the claimant id when starting work */
  id: string;
  displayName: string;
  roleIds: string[];
  /** `data.key` of each included role, e.g. "inspector" */
  roleKeys: string[];
}

type DirectoryUserRecord = SorRecord<{ displayName?: string; roleIds?: string[] }> & {
  related?: { roles?: { id: string; data?: { key?: string } }[] | null };
};

/** null when the user has no directory profile yet */
export function parseMyUser(records: DirectoryUserRecord[] | undefined): MyWorkUser | null {
  const record = records?.[0];
  if (!record) {
    return null;
  }
  const roleIds = record.data?.roleIds ?? record.related?.roles?.map((role) => role.id) ?? [];
  return {
    id: record.id,
    displayName: record.data?.displayName ?? record.display ?? '',
    roleIds: Array.from(new Set(roleIds.filter(Boolean))),
    roleKeys: (record.related?.roles ?? [])
      .map((role) => role.data?.key)
      .filter((key): key is string => !!key),
  };
}

/** Entity types that are switched on for work queues, with their definition for the button label */
export function buildCreatableEntitiesQuery(): SorQueryPayload {
  return {
    definition: QUEUE_ENTITY_DEFINITION,
    filter: { field: 'data.enabled', op: 'eq', value: true },
    expand: ['definition'],
    page: { limit: 25 },
  };
}

/** One "Create {display}" button per entity whose `data.enabled` is true */
export function parseCreatableEntities(records: QueueEntityRecord[]): WorkQueueCreateOption[] {
  return records
    .filter((record) => record.data?.enabled === true)
    .map((record) => ({
      definitionId: record.data.definitionId,
      key: record.expanded?.definition?.key ?? '',
      label: record.expanded?.definition?.display ?? '',
    }))
    .filter((option) => option.label);
}

// ---------------------------------------------------------------------------------------------
// A3: which queues do I reach
// ---------------------------------------------------------------------------------------------

export function buildMyMembershipsQuery(roleIds: string[], now = sorNow()): SorQueryPayload {
  return {
    definition: QUEUE_MEMBER_DEFINITION,
    filter: {
      and: [
        { field: 'enabled', op: 'eq', value: true },
        {
          or: [
            { field: 'effectiveFrom', op: 'isnull' },
            { field: 'effectiveFrom', op: 'lte', value: now },
          ],
        },
        {
          or: [
            { field: 'effectiveUntil', op: 'isnull' },
            { field: 'effectiveUntil', op: 'gt', value: now },
          ],
        },
        {
          or: [
            { field: 'user.subjectId', op: 'eq', value: PRINCIPAL_ID },
            // Dropped when the user holds no roles
            ...(roleIds.length ? [{ field: 'roleId', op: 'in', value: roleIds }] : []),
            // My direct reports' memberships, so My team can list their work
            { field: 'user.manager.subjectId', op: 'eq', value: PRINCIPAL_ID },
          ],
        },
      ],
    },
    select: ['id', 'data.queueId', 'data.subjectType', 'data.userId', 'data.roleId', 'data.queueRole'],
    page: { limit: 500 },
  };
}

export interface MyWorkMemberships {
  /** Every queue reached, including my reports' queues: the `queueId in` condition of every list */
  queueIds: string[];
  /** My own role per queue (highest when several rows apply) */
  roleByQueue: Record<string, QueueRole>;
  /** My own USER membership per queue: the assignee when I claim an item there */
  membershipByQueue: Record<string, string>;
}

export function parseMemberships(rows: SorProjectedRow[], user: MyWorkUser): MyWorkMemberships {
  const queueIds = new Set<string>();
  const roleByQueue: Record<string, QueueRole> = {};
  const membershipByQueue: Record<string, string> = {};
  const myRoleIds = new Set(user.roleIds);
  for (const row of rows) {
    const queueId = asString(row['data.queueId']);
    if (!queueId) {
      continue;
    }
    queueIds.add(queueId);
    const userId = asString(row['data.userId']);
    const roleId = asString(row['data.roleId']);
    const isMine = userId === user.id || (!!roleId && myRoleIds.has(roleId));
    if (!isMine) {
      // A report's row: their queue is listed, but it gives me no role there
      continue;
    }
    const queueRole = (asString(row['data.queueRole']) ?? 'AGENT') as QueueRole;
    if (!roleByQueue[queueId] || QUEUE_ROLE_RANK[queueRole] > QUEUE_ROLE_RANK[roleByQueue[queueId]]) {
      roleByQueue[queueId] = queueRole;
    }
    if (userId === user.id) {
      membershipByQueue[queueId] = row.id;
    }
  }
  return { queueIds: Array.from(queueIds), roleByQueue, membershipByQueue };
}

// ---------------------------------------------------------------------------------------------
// B1: what do my queues route
// ---------------------------------------------------------------------------------------------

export function buildMyQueuesQuery(queueIds: string[]): SorQueryPayload {
  return {
    definition: QUEUE_DEFINITION,
    filter: { field: 'id', op: 'in', value: queueIds },
    select: ['id', 'data.queueKey', 'data.assignmentPolicies', 'data.entityTypes'],
    include: ['policy_source_definitions'],
    page: { limit: Math.max(queueIds.length, 1) },
  };
}

/** One entity type my queues route: one tab in the view */
export interface MyWorkType {
  /** Source definition id: the `sourceDefinitionId` pin of every list */
  definitionId: string;
  /** Definition key for GET /definitions/{key}, e.g. "slm.insp.inspection" */
  key: string;
  name: string;
  /** Purposes routed for this type, e.g. ["inspection_review"] */
  purposes: string[];
}

interface QueueAssignmentPolicy {
  enabled?: boolean;
  purpose?: string;
  sourceDefinitionId?: string;
}

interface PolicySourceDefinition {
  id: string;
  key?: string;
  name?: string;
  display?: string;
}

/** Enabled policies of each queue, minus types the queue switches off in `entityTypes`; sorted by name */
export function parseQueueTypes(rows: SorProjectedRow[]): MyWorkType[] {
  const types = new Map<string, MyWorkType>();
  for (const row of rows) {
    const policies = (row['data.assignmentPolicies'] as QueueAssignmentPolicy[] | null) ?? [];
    const entityTypes = (row['data.entityTypes'] as { enabled?: boolean; sourceDefinitionId?: string }[] | null) ?? [];
    const disabled = new Set(
      entityTypes.filter((type) => type.enabled === false).map((type) => type.sourceDefinitionId)
    );
    const definitions = (row.related?.['policy_source_definitions'] as unknown as PolicySourceDefinition[] | null) ?? [];
    for (const policy of policies) {
      const definitionId = policy.sourceDefinitionId;
      if (!policy.enabled || !definitionId || disabled.has(definitionId)) {
        continue;
      }
      const definition = definitions.find((candidate) => candidate.id === definitionId);
      if (!definition?.key) {
        continue;
      }
      const type = types.get(definitionId) ?? {
        definitionId,
        key: definition.key,
        name: definition.name || definition.display || definition.key,
        purposes: [],
      };
      if (policy.purpose && !type.purposes.includes(policy.purpose)) {
        type.purposes.push(policy.purpose);
      }
      types.set(definitionId, type);
    }
  }
  return Array.from(types.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------------------------
// B2: how much work per type (optional tab counts)
// ---------------------------------------------------------------------------------------------

export function buildTypeCountsQuery(queueIds: string[]): SorQueryPayload {
  return {
    definition: QUEUE_ITEM_DEFINITION,
    filter: { field: 'queueId', op: 'in', value: queueIds },
    facets: [{ field: 'sourceDefinitionId', limit: 100 }, { field: 'purpose', limit: 100 }],
    select: ['id'],
    page: { limit: 1 },
  };
}

/** definitionId -> number of queue items (every state) */
export function parseTypeCounts(facets: Record<string, SorFacet> | null | undefined): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const bucket of facets?.['sourceDefinitionId']?.buckets ?? []) {
    if (typeof bucket.value === 'string') {
      counts[bucket.value] = bucket.count;
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------------------------
// A1: the selected type (GET /definitions/{key})
// ---------------------------------------------------------------------------------------------

/** Record progress, from the definition's `state_info[...].category` */
export type StateCategory = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

export interface MyWorkTypeState {
  key: string;
  label: string;
  category: StateCategory | null;
}

export interface MyWorkTypeDefinition {
  id: string;
  key: string;
  name: string;
  states: MyWorkTypeState[];
  terminalStates: string[];
  /** States the "current item only" condition uses */
  nonTerminalStates: string[];
  canUpdate: boolean;
  canCreate: boolean;
}

/** "IN_PROGRESS" -> "In Progress" */
export function humanizeState(state: string): string {
  return state
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

interface DefinitionOut {
  id: string;
  key: string;
  name?: string;
  lifecycle?: {
    states?: string[];
    terminal?: string[];
    state_info?: Record<string, { label?: string | null; category?: StateCategory | null }> | null;
  } | null;
  permissions?: { can_update?: boolean; can_create?: boolean } | null;
}

export function parseTypeDefinition(definition: DefinitionOut): MyWorkTypeDefinition {
  const states = definition.lifecycle?.states ?? [];
  const terminal = definition.lifecycle?.terminal ?? [];
  const info = definition.lifecycle?.state_info ?? {};
  return {
    id: definition.id,
    key: definition.key,
    name: definition.name ?? definition.key,
    states: states.map((state) => ({
      key: state,
      label: info[state]?.label || humanizeState(state),
      category: info[state]?.category ?? null,
    })),
    terminalStates: terminal,
    nonTerminalStates: states.filter((state) => !terminal.includes(state)),
    canUpdate: definition.permissions?.can_update ?? false,
    canCreate: definition.permissions?.can_create ?? false,
  };
}

// ---------------------------------------------------------------------------------------------
// A4: actions on my queues
// ---------------------------------------------------------------------------------------------

export type QueueActionKind = 'QUEUE_OPERATION' | 'ENTITY_TRANSITION' | 'ENTITY_UPDATE' | 'MANUAL_ACKNOWLEDGEMENT';

/** The assignment steps of §3 */
export type QueueOperation = 'CLAIM' | 'ASSIGN' | 'START' | 'WAIT' | 'RESUME' | 'RELEASE';

export const QUEUE_OPERATIONS: QueueOperation[] = ['CLAIM', 'ASSIGN', 'START', 'WAIT', 'RESUME', 'RELEASE'];

export interface QueueActionData {
  queueId: string;
  actionKey: string;
  label: string;
  kind: QueueActionKind;
  queueOperation?: string | null;
  targetState?: string | null;
  requiredQueueRole?: 'AGENT' | 'SUPERVISOR';
  allowedItemStates?: string[];
  allowedSourceStates?: string[];
  sourceDefinitionId?: string | null;
  requireComment?: boolean;
  editableEntityFields?: string[];
  enabled?: boolean;
}

export interface MyWorkAction {
  id: string;
  data: QueueActionData;
}

export function buildQueueActionsQuery(queueIds: string[]): SorQueryPayload {
  return {
    definition: QUEUE_ACTION_DEFINITION,
    filter: {
      and: [
        { field: 'queueId', op: 'in', value: queueIds },
        { field: 'enabled', op: 'eq', value: true },
      ],
    },
    page: { limit: 500 },
  };
}

export function parseQueueActions(records: SorRecord<QueueActionData>[]): MyWorkAction[] {
  return records
    .filter((record) => record.data?.queueId && record.data.enabled !== false)
    .map((record) => ({ id: record.id, data: record.data }));
}

// ---------------------------------------------------------------------------------------------
// §2: the list
// ---------------------------------------------------------------------------------------------

export type MyWorkSort = 'newest' | 'oldest';

/**
 * Source fields the list reads, as paths through `source`. The defaults are the inspection's; pass
 * another set for a type with different fields.
 */
export interface MyWorkFieldConfig {
  /** The number on the card, e.g. the WIS request number */
  number: string;
  /** Second identifier, also matched by search */
  secondaryNumber: string | null;
  customer: string | null;
  location: string | null;
  createdAt: string;
}

export const DEFAULT_MY_WORK_FIELDS: MyWorkFieldConfig = {
  number: 'subject.externalId',
  secondaryNumber: 'inspectionNumber',
  customer: 'customer.name',
  location: 'location.address',
  createdAt: 'created_at',
};

export interface MyWorkListCriteria {
  type: MyWorkType;
  definition: MyWorkTypeDefinition;
  queueIds: string[];
  statusCategory?: StateCategory | null;
  sourceStates?: string[];
  customer?: string | null;
  search?: string;
  sort?: MyWorkSort;
  fields?: MyWorkFieldConfig;
}

const sourcePath = (field: string) => `source.${field}`;

/** The list query for one screen state (without `page`): one request returns the page, total and tiles */
export function buildMyWorkListQuery(criteria: MyWorkListCriteria): Omit<SorQueryPayload, 'page'> {
  const fields = criteria.fields ?? DEFAULT_MY_WORK_FIELDS;
  const search = criteria.search?.trim();
  const sourceSelect = [
    fields.number,
    fields.secondaryNumber,
    fields.customer,
    fields.location,
    fields.createdAt,
    'state',
    'revision',
    'external_id',
  ].filter((field): field is string => !!field);

  return {
    definition: QUEUE_ITEM_DEFINITION,
    filter: combineSorFilters([
      // The pin: any path through `source` needs it
      { field: 'sourceDefinitionId', op: 'eq', value: criteria.definition.id },
      { field: 'purpose', op: 'in', value: criteria.type.purposes },
      { field: 'queueId', op: 'in', value: criteria.queueIds },
      // Current item only: an open item, or a finished item whose record is finished. Hides earlier
      // cycles of reworked work; a type without a lifecycle has no cycles to hide
      criteria.definition.nonTerminalStates.length > 0 && {
        or: [
          { field: 'state_category', op: 'ne', value: 'DONE' },
          { not: { field: 'source.state', op: 'in', value: criteria.definition.nonTerminalStates } },
        ],
      },
      !!criteria.statusCategory && {
        field: 'source.state_category',
        op: 'eq',
        value: criteria.statusCategory,
      },
      !!criteria.sourceStates?.length && { field: 'source.state', op: 'in', value: criteria.sourceStates },
      !!criteria.customer && !!fields.customer && {
        field: sourcePath(fields.customer),
        op: 'eq',
        value: criteria.customer,
      },
      !!search && {
        or: [
          { field: sourcePath(fields.number), op: 'startswith', value: search },
          ...(fields.secondaryNumber
            ? [{ field: sourcePath(fields.secondaryNumber), op: 'contains', value: search }]
            : []),
        ],
      },
    ]),
    select: [
      'id',
      'state',
      'revision',
      'data.queueId',
      'data.queueVisitId',
      'data.assignedAt',
      'data.assigneeMemberId',
      'data.claimedByUserId',
      'data.claimedAt',
      'data.startedAt',
      'data.waitingReason',
      'assignee.user.displayName',
      'assignee.role.name',
      'claimant.displayName',
      ...sourceSelect.map(sourcePath),
    ],
    facets: [
      { field: 'source.state_category' },
      ...(fields.customer ? [{ field: sourcePath(fields.customer), limit: 100 }] : []),
    ],
    // Newest is the default order (cursor paging); oldest sorts through `source` and pages by offset
    ...(criteria.sort === 'oldest' ? { sort: [{ field: sourcePath(fields.createdAt), dir: 'asc' as const }] } : {}),
  };
}

/** One queue item as the card shows it */
export interface MyWorkItem {
  id: string;
  /** Queue item state: READY | ASSIGNED | IN_PROGRESS | WAITING | COMPLETED | CANCELLED */
  state: string;
  revision: number;
  queueId: string;
  queueVisitId: string;
  assignedAt: string | null;
  assigneeMemberId: string | null;
  claimedByUserId: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  waitingReason: string | null;
  /** The assigned user, or the assigned role's name */
  assigneeName: string | null;
  claimantName: string | null;
  source: {
    id: string;
    definition: string;
    state: string | null;
    revision: number;
    number: string;
    secondaryNumber: string | null;
    customer: string | null;
    location: string | null;
    createdAt: string | null;
    /** The record's external_id, e.g. `aiworker_usage:<id>` for records an AI worker created */
    externalId: string | null;
  } | null;
}

export function mapMyWorkItem(row: SorProjectedRow, fields: MyWorkFieldConfig = DEFAULT_MY_WORK_FIELDS): MyWorkItem {
  const assignee = asRelated(row.related?.['assignee']);
  const assigneeUser = asRelated(assignee?.related?.['user']);
  const assigneeRole = asRelated(assignee?.related?.['role']);
  const claimant = asRelated(row.related?.['claimant']);
  const source = asRelated(row.related?.['source']);
  const readSource = (field: string | null) => (field && source ? asString(source[field]) : null);
  return {
    id: row.id,
    state: asString(row['state']) ?? '',
    revision: asNumber(row['revision']) ?? 0,
    queueId: asString(row['data.queueId']) ?? '',
    queueVisitId: asString(row['data.queueVisitId']) ?? '',
    assignedAt: asString(row['data.assignedAt']),
    assigneeMemberId: asString(row['data.assigneeMemberId']),
    claimedByUserId: asString(row['data.claimedByUserId']),
    claimedAt: asString(row['data.claimedAt']),
    startedAt: asString(row['data.startedAt']),
    waitingReason: asString(row['data.waitingReason']),
    assigneeName:
      formatDirectoryName(asString(assigneeUser?.['displayName'])) || asString(assigneeRole?.['name']),
    claimantName: formatDirectoryName(asString(claimant?.['displayName'])) || null,
    source: source
      ? {
          id: source.id,
          definition: asString(source.definition) ?? '',
          state: asString(source['state']),
          revision: asNumber(source['revision']) ?? 0,
          number: readSource(fields.number) ?? readSource(fields.secondaryNumber) ?? source.id,
          secondaryNumber: readSource(fields.secondaryNumber),
          customer: readSource(fields.customer),
          location: readSource(fields.location),
          createdAt: readSource(fields.createdAt),
          externalId: asString(source['external_id']),
        }
      : null,
  };
}

export interface FacetOption {
  value: string;
  count: number;
}

/** Non-empty string buckets of a facet, e.g. the customer dropdown */
export function facetOptions(facets: Record<string, SorFacet> | null | undefined, field: string): FacetOption[] {
  return (facets?.[field]?.buckets ?? [])
    .filter((bucket): bucket is { value: string; count: number } => typeof bucket.value === 'string' && !!bucket.value)
    .map((bucket) => ({ value: bucket.value, count: bucket.count }));
}

/** Count per facet value, e.g. { READY: 21, CANCELLED: 13 } */
export function facetCounts(facets: Record<string, SorFacet> | null | undefined, field: string): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const bucket of facets?.[field]?.buckets ?? []) {
    if (typeof bucket.value === 'string') {
      counts[bucket.value] = bucket.count;
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------------------------
// §3: assigning work
// ---------------------------------------------------------------------------------------------

export type QueueItemHistoryEvent =
  | 'CLAIMED'
  | 'ASSIGNED'
  | 'STARTED'
  | 'WAITING'
  | 'RESUMED'
  | 'RELEASED'
  | 'ACTION_SUCCEEDED';

export interface QueueOperationStep {
  /** PATCH /records/{itemId} `data` */
  data: Record<string, unknown>;
  /** POST /records/{itemId}/transition `to` */
  to: string;
  eventType: QueueItemHistoryEvent;
}

export interface QueueOperationInput {
  /** CLAIM: my membership in the item's queue; ASSIGN: the chosen member's */
  memberId?: string | null;
  /** START: my directory user id */
  userId?: string | null;
  /** WAIT: why the work is on hold */
  waitingReason?: string | null;
  at?: string;
}

/** The PATCH fields, target state and history event of one assignment step */
export function buildQueueOperationStep(operation: QueueOperation, input: QueueOperationInput = {}): QueueOperationStep {
  const at = input.at ?? sorNow();
  switch (operation) {
    case 'CLAIM':
      return { data: { assigneeMemberId: input.memberId, assignedAt: at }, to: 'ASSIGNED', eventType: 'CLAIMED' };
    case 'ASSIGN':
      return { data: { assigneeMemberId: input.memberId, assignedAt: at }, to: 'ASSIGNED', eventType: 'ASSIGNED' };
    case 'START':
      return {
        data: { claimedByUserId: input.userId, claimedAt: at, startedAt: at },
        to: 'IN_PROGRESS',
        eventType: 'STARTED',
      };
    case 'WAIT':
      return { data: { waitingReason: input.waitingReason }, to: 'WAITING', eventType: 'WAITING' };
    case 'RESUME':
      return { data: { waitingReason: null }, to: 'IN_PROGRESS', eventType: 'RESUMED' };
    case 'RELEASE':
      return { data: RELEASE_DATA, to: 'READY', eventType: 'RELEASED' };
  }
}

/** Clears every assignment field: the Release PATCH, also used to undo a claim whose transition failed */
export const RELEASE_DATA: Record<string, null> = {
  assigneeMemberId: null,
  assignedAt: null,
  claimedByUserId: null,
  claimedAt: null,
  startedAt: null,
  waitingReason: null,
};

/**
 * PATCH data that puts back what a step's PATCH changed, from the item as it was listed. Used when
 * the step's transition is refused, so the item isn't left in its old state with the new fields
 * (e.g. still WAITING but with its assignment cleared).
 */
export function buildStepRollbackData(step: QueueOperationStep, item: MyWorkItem): Record<string, unknown> {
  const previous: Record<string, unknown> = {
    assigneeMemberId: item.assigneeMemberId,
    assignedAt: item.assignedAt,
    claimedByUserId: item.claimedByUserId,
    claimedAt: item.claimedAt,
    startedAt: item.startedAt,
    waitingReason: item.waitingReason,
  };
  return Object.fromEntries(Object.keys(step.data).map((field) => [field, previous[field] ?? null]));
}

export interface QueueAssigneeSnapshot {
  memberId?: string | null;
  userId?: string | null;
}

export interface QueueItemHistoryInput {
  eventId: string;
  eventType: QueueItemHistoryEvent;
  item: { id: string; revision: number; queueId: string; queueVisitId: string };
  fromState?: string | null;
  toState?: string | null;
  toAssignee?: QueueAssigneeSnapshot | null;
  fromAssignee?: QueueAssigneeSnapshot | null;
  actionId?: string | null;
  actionOutcome?: 'SUCCEEDED' | 'FAILED' | null;
  reason?: string | null;
  occurredAt?: string;
}

const compact = <T extends Record<string, unknown>>(value: T): Partial<T> =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null)) as Partial<T>;

/** POST /records?definition=core.workqueue.queue_item_history body; one UUID is both `external_id` and `eventId` */
export function buildQueueItemHistoryRecord(input: QueueItemHistoryInput): { external_id: string; data: Record<string, unknown> } {
  return {
    external_id: input.eventId,
    data: compact({
      eventId: input.eventId,
      eventType: input.eventType,
      queueItemId: input.item.id,
      itemRevision: input.item.revision,
      queueId: input.item.queueId,
      queueVisitId: input.item.queueVisitId,
      occurredAt: input.occurredAt ?? sorNow(),
      fromState: input.fromState,
      toState: input.toState,
      toAssignee: input.toAssignee ? compact({ ...input.toAssignee }) : undefined,
      fromAssignee: input.fromAssignee ? compact({ ...input.fromAssignee }) : undefined,
      actionId: input.actionId,
      actionOutcome: input.actionOutcome,
      reason: input.reason,
    }),
  };
}

/** Finds a history record by its eventId, to check a POST whose response was lost */
export function buildQueueItemHistoryLookupQuery(eventId: string): SorQueryPayload {
  return {
    definition: QUEUE_ITEM_HISTORY_DEFINITION,
    filter: { field: 'eventId', op: 'eq', value: eventId },
    select: ['id'],
    page: { limit: 1 },
  };
}

/** Members of one queue, with names, for the Assign picker */
export function buildQueueMembersQuery(queueId: string): SorQueryPayload {
  return {
    definition: QUEUE_MEMBER_DEFINITION,
    filter: {
      and: [
        { field: 'queueId', op: 'eq', value: queueId },
        { field: 'enabled', op: 'eq', value: true },
      ],
    },
    select: ['id', 'data.subjectType', 'data.userId', 'data.roleId', 'data.queueRole', 'user.displayName', 'role.name'],
    page: { limit: 500 },
  };
}

export interface QueueMemberOption {
  /** Queue membership id: the ASSIGN `assigneeMemberId` */
  id: string;
  userId: string | null;
  label: string;
  queueRole: QueueRole;
}

export function parseQueueMembers(rows: SorProjectedRow[]): QueueMemberOption[] {
  return rows
    .filter((row) => asString(row['data.queueRole']) !== 'OBSERVER')
    .map((row) => {
      const user = asRelated(row.related?.['user']);
      const role = asRelated(row.related?.['role']);
      return {
        id: row.id,
        userId: asString(row['data.userId']),
        label:
          formatDirectoryName(asString(user?.['displayName'])) ||
          asString(role?.['name']) ||
          asString(row['data.roleId']) ||
          row.id,
        queueRole: (asString(row['data.queueRole']) ?? 'AGENT') as QueueRole,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------------------------
// Which actions show on a card
// ---------------------------------------------------------------------------------------------

export interface ActionGateContext {
  roleByQueue: Record<string, QueueRole>;
  membershipByQueue: Record<string, string>;
  definition: MyWorkTypeDefinition;
}

/**
 * A4 has the action for the item's queue and type, the item's state is in `allowedItemStates`, the
 * source's state is in `allowedSourceStates` (when set), the user's queue role qualifies, and for
 * actions on the record A1 grants `can_update`.
 */
export function isActionAvailable(action: MyWorkAction, item: MyWorkItem, context: ActionGateContext): boolean {
  const data = action.data;
  if (data.queueId !== item.queueId) {
    return false;
  }
  if (data.sourceDefinitionId && data.sourceDefinitionId !== context.definition.id) {
    return false;
  }
  if (!data.allowedItemStates?.includes(item.state)) {
    return false;
  }
  if (data.allowedSourceStates?.length && !data.allowedSourceStates.includes(item.source?.state ?? '')) {
    return false;
  }
  if (!queueRoleMeets(context.roleByQueue[item.queueId], data.requiredQueueRole)) {
    return false;
  }
  if (data.kind === 'QUEUE_OPERATION') {
    // Claiming needs my own membership in the queue
    return data.queueOperation !== 'CLAIM' || !!context.membershipByQueue[item.queueId];
  }
  return context.definition.canUpdate && !!item.source;
}

/** The item's available actions, queue operations first in §3 order */
export function getAvailableActions(
  actions: MyWorkAction[],
  item: MyWorkItem,
  context: ActionGateContext
): MyWorkAction[] {
  const operationRank = (action: MyWorkAction) => {
    const index = QUEUE_OPERATIONS.indexOf(action.data.queueOperation as QueueOperation);
    return action.data.kind === 'QUEUE_OPERATION' && index >= 0 ? index : QUEUE_OPERATIONS.length;
  };
  return actions
    .filter((action) => isActionAvailable(action, item, context))
    .filter(
      (action) =>
        action.data.kind !== 'QUEUE_OPERATION' ||
        QUEUE_OPERATIONS.includes(action.data.queueOperation as QueueOperation)
    )
    .sort((a, b) => operationRank(a) - operationRank(b));
}
